import { useState, useCallback } from 'react'
import { SearchForm } from './components/SearchForm'
import { ResultCard } from './components/ResultCard'
import { SummaryView } from './components/SummaryView'
import ApiSettings from './components/ApiSettings'
import { useSymptomSearch } from './hooks/useSymptomSearch'
import type { PetType, AiConfig } from './lib/types'

type Step = 'search' | 'results' | 'summary'

export default function App() {
  const [step, setStep] = useState<Step>('search')
  const [selectedUrls, setSelectedUrls] = useState<string[]>([])
  const [currentKeyword, setCurrentKeyword] = useState('')
  const [currentPetType, setCurrentPetType] = useState<PetType>('dog')
  const [aiConfig, setAiConfig] = useState<AiConfig | null>(null)

  const handleAiConfigChange = useCallback((config: AiConfig | null) => {
    setAiConfig(config)
  }, [])

  const {
    isSearching,
    isSummarizing,
    isGeneratingPdf,
    results,
    summary,
    error,
    search,
    summarize,
    downloadPdf,
    reset,
  } = useSymptomSearch()

  const handleSearch = async (keyword: string, petType: PetType) => {
    setCurrentKeyword(keyword)
    setCurrentPetType(petType)
    setSelectedUrls([])
    await search(keyword, petType)
    setStep('results')
  }

  const handleSelectUrl = (url: string) => {
    setSelectedUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    )
  }

  const handleSummarize = async () => {
    if (selectedUrls.length === 0 || !aiConfig) return
    await summarize(selectedUrls, currentKeyword, currentPetType, aiConfig)
    setStep('summary')
  }

  const handleReset = () => {
    reset()
    setStep('search')
    setSelectedUrls([])
    setCurrentKeyword('')
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg paw-pattern transition-colors relative overflow-hidden">
      {/* 背景デコレーション */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 text-6xl opacity-10 animate-float">🐕</div>
        <div className="absolute top-40 right-20 text-5xl opacity-10 animate-float" style={{ animationDelay: '1s' }}>🐈</div>
        <div className="absolute bottom-40 left-20 text-4xl opacity-10 animate-float" style={{ animationDelay: '2s' }}>🐾</div>
        <div className="absolute bottom-20 right-10 text-5xl opacity-10 animate-float" style={{ animationDelay: '0.5s' }}>🦴</div>
        <div className="absolute top-1/2 left-1/4 text-3xl opacity-5 animate-wiggle">💕</div>
        <div className="absolute top-1/3 right-1/3 text-3xl opacity-5 animate-wiggle" style={{ animationDelay: '0.5s' }}>🐾</div>
      </div>

      {/* ヘッダー */}
      <header className="relative z-10 bg-white/80 dark:bg-dark-bg-card/80 backdrop-blur-md shadow-cute border-b-4 border-light-accent dark:border-dark-accent">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 group"
            >
              <span className="text-3xl group-hover:animate-wiggle transition-transform">🐾</span>
              <div>
                <h1 className="text-xl font-bold gradient-text">
                  ペット症状まとめ
                </h1>
                <p className="text-xs text-light-text-sub dark:text-dark-text-sub">
                  大切な家族の健康をサポート
                </p>
              </div>
            </button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="relative z-10 max-w-2xl mx-auto px-4 py-8">
        {/* エラー表示 */}
        {error && (
          <div className="mb-6 p-4 rounded-2xl bg-light-error/50 dark:bg-dark-error/20 border-2 border-light-error dark:border-dark-error shadow-cute animate-paw-print">
            <div className="flex items-center gap-3">
              <span className="text-2xl">😿</span>
              <p className="text-red-700 dark:text-dark-error font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* ステップ: 検索 */}
        {step === 'search' && (
          <div className="space-y-6">
            {/* ウェルカムメッセージ */}
            <div className="text-center py-6">
              <div className="inline-flex items-center gap-2 text-4xl mb-4 animate-bounce-slow">
                <span>🐕</span>
                <span>🐈</span>
                <span>🐰</span>
              </div>
              <h2 className="text-2xl font-bold text-light-text dark:text-dark-text mb-2">
                ペットの症状が気になったら
              </h2>
              <p className="text-light-text-sub dark:text-dark-text-sub">
                キーワードを入力して、獣医さんのサイトから情報をまとめます
              </p>
            </div>

            {/* AI設定 */}
            <ApiSettings onConfigChange={handleAiConfigChange} disabled={isSearching} />

            <div className="bg-white/90 dark:bg-dark-bg-card/90 backdrop-blur-sm rounded-3xl shadow-cute p-6 border-2 border-light-paw/30 dark:border-dark-paw/30 card-cute">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">🔍</span>
                <h2 className="text-lg font-bold text-light-text dark:text-dark-text">
                  症状を検索
                </h2>
              </div>
              <p className="text-sm text-light-text-sub dark:text-dark-text-sub mb-6 pl-9">
                ペットの種類と症状キーワードを入力してね
              </p>
              <SearchForm onSearch={handleSearch} isLoading={isSearching} />
            </div>

            {/* ヒント */}
            <div className="bg-pet-lavender/30 dark:bg-pet-lavender/10 rounded-2xl p-4 border border-pet-lavender">
              <div className="flex items-start gap-3">
                <span className="text-xl">💡</span>
                <div className="text-sm text-light-text-sub dark:text-dark-text-sub">
                  <p className="font-medium mb-1">検索のコツ</p>
                  <p>「下痢」「嘔吐」「元気がない」など、気になる症状を入力してみてね</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ステップ: 検索結果 */}
        {step === 'results' && (
          <div className="space-y-6">
            <div className="bg-white/90 dark:bg-dark-bg-card/90 backdrop-blur-sm rounded-3xl shadow-cute p-6 border-2 border-light-paw/30 dark:border-dark-paw/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📋</span>
                  <h2 className="text-lg font-bold text-light-text dark:text-dark-text">
                    検索結果
                  </h2>
                </div>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1 text-sm text-light-accent dark:text-dark-accent hover:underline font-medium"
                >
                  <span>←</span> 検索に戻る
                </button>
              </div>

              {results.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-5xl mb-4 block">🔍</span>
                  <p className="text-light-text-sub dark:text-dark-text-sub">
                    検索結果が見つかりませんでした
                  </p>
                  <p className="text-sm text-light-text-sub dark:text-dark-text-sub mt-2">
                    別のキーワードで試してみてね
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-pet-cream/50 dark:bg-pet-cream/10 rounded-xl p-3 mb-4">
                    <p className="text-sm text-light-text dark:text-dark-text flex items-center gap-2">
                      <span>✨</span>
                      要約したいページを選んでね（最大3つ）
                    </p>
                  </div>
                  <div className="space-y-3">
                    {results.map((result) => (
                      <ResultCard
                        key={result.url}
                        result={result}
                        isSelected={selectedUrls.includes(result.url)}
                        onSelect={handleSelectUrl}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {selectedUrls.length > 0 && (
              <div className="space-y-3">
                {!aiConfig && (
                  <div className="bg-light-warning/50 dark:bg-dark-warning/20 rounded-2xl p-4 border border-light-warning dark:border-dark-warning">
                    <p className="text-center text-amber-700 dark:text-dark-warning text-sm flex items-center justify-center gap-2">
                      <span>⚠️</span>
                      要約するにはAI設定が必要だよ
                    </p>
                  </div>
                )}
                <button
                  onClick={handleSummarize}
                  disabled={isSummarizing || !aiConfig}
                  className="
                    w-full py-4 px-6 rounded-2xl font-bold text-lg
                    bg-gradient-to-r from-light-accent to-pet-coral
                    dark:from-dark-accent dark:to-pet-coral
                    text-white shadow-cute
                    hover:shadow-cute-lg hover:scale-[1.02]
                    active:scale-[0.98]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                    transition-all duration-300
                    flex items-center justify-center gap-3
                  "
                >
                  {isSummarizing ? (
                    <>
                      <span className="loading-paw">🐾</span>
                      <span className="loading-paw">🐾</span>
                      <span className="loading-paw">🐾</span>
                      <span className="ml-2">まとめ中...</span>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl">✨</span>
                      {selectedUrls.length}件をまとめる
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ステップ: 要約表示 */}
        {step === 'summary' && summary && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep('results')}
                className="flex items-center gap-1 text-sm text-light-accent dark:text-dark-accent hover:underline font-medium"
              >
                <span>←</span> 検索結果に戻る
              </button>
              <button
                onClick={handleReset}
                className="flex items-center gap-2 text-sm text-light-text-sub dark:text-dark-text-sub hover:text-light-accent dark:hover:text-dark-accent font-medium"
              >
                <span>🔍</span> 新しく検索
              </button>
            </div>

            <div className="bg-white/90 dark:bg-dark-bg-card/90 backdrop-blur-sm rounded-3xl shadow-cute p-6 border-2 border-light-paw/30 dark:border-dark-paw/30">
              <SummaryView
                summary={summary}
                onDownloadPdf={(userMemo) => downloadPdf(summary, userMemo)}
                isGeneratingPdf={isGeneratingPdf}
              />
            </div>
          </div>
        )}
      </main>

      {/* フッター */}
      <footer className="relative z-10 py-8 text-center">
        <div className="flex items-center justify-center gap-2 text-2xl mb-3">
          <span>🐕</span>
          <span>💕</span>
          <span>🐈</span>
        </div>
        <p className="text-sm text-light-text-sub dark:text-dark-text-sub">
          このサービスは参考情報です
        </p>
        <p className="text-sm text-light-text-sub dark:text-dark-text-sub">
          症状が続く場合は必ず獣医師さんにご相談ください 🏥
        </p>
      </footer>
    </div>
  )
}

/**
 * ダークモード切り替えボタン
 */
function ThemeToggle() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark')
    }
    return false
  })

  const toggle = () => {
    const newIsDark = !isDark
    setIsDark(newIsDark)
    document.documentElement.classList.toggle('dark', newIsDark)
    localStorage.setItem('theme', newIsDark ? 'dark' : 'light')
  }

  return (
    <button
      onClick={toggle}
      className="p-3 rounded-full bg-light-bg-sub dark:bg-dark-bg-sub hover:bg-light-paw/50 dark:hover:bg-dark-paw/30 transition-all duration-300 hover:scale-110 active:scale-95 shadow-card"
      aria-label="テーマ切り替え"
    >
      {isDark ? (
        <span className="text-xl">🌙</span>
      ) : (
        <span className="text-xl">☀️</span>
      )}
    </button>
  )
}
